/* ==========================================================================
   Ray Rider — Range Calculator core (pure math, no DOM)
   --------------------------------------------------------------------------
   Single source of truth for the trust-critical numbers. Loaded in the
   browser via {{ 'rr-range-calc.js' | asset_url }} (attaches window.RRCalc)
   and imported by tests/range-calc.test.ts in node (module.exports).

   Model (matches the live data backend, NOT the prototype's 40-ZIP table):
     - assets/solar-data.json: 2082-point PVWatts grid + ~50 ZIP overrides.
       Each point holds kwh_day_{avg,summer,winter} — the daily output of the
       Ray Rider 0.30 kW system (3x100W, 10 deg south tilt, 14% losses) already
       baked in. So we multiply by miles-per-kWh; we do NOT re-derive from sun.
     - assets/zip-to-latlon.json: 33k ZIPs -> {lat,lon,city?,state?}.

   Resolution:
     override hit          -> use precomputed kwh for that ZIP
     else nearest grid pt  -> haversine over the 2082-point grid
     else (no lat/lon)     -> null  (controller shows a graceful message)

   Display "exact":
     true  when we have a city label for the ZIP  -> "Found {City, ST}"
     false otherwise (coords but no name)         -> "estimated for your area"
     The grid is dense enough that nearest-point is still genuinely local;
     the only thing we lack without a city is the place NAME, not accuracy.
   ========================================================================== */
(function () {
  // Miles per kWh by battery chemistry. Lithium converts stored energy to
  // distance more efficiently than lead-acid. Accept both 'lead' and the
  // section's legacy 'lead-acid' key.
  var MI_PER_KWH = { lead: 10.5, 'lead-acid': 10.5, lithium: 15 };
  var EARTH_MI = 3959;

  function validZip(zip) {
    return /^\d{5}$/.test(String(zip == null ? '' : zip).trim());
  }

  function round1(n) {
    return Math.round(n * 10) / 10;
  }

  function milesPerKwh(battery) {
    return MI_PER_KWH[battery] || MI_PER_KWH.lead;
  }

  // Great-circle distance in miles between two lat/lon points.
  function haversine(la1, lo1, la2, lo2) {
    var dLa = (la2 - la1) * Math.PI / 180;
    var dLo = (lo2 - lo1) * Math.PI / 180;
    var a = Math.sin(dLa / 2) * Math.sin(dLa / 2) +
            Math.cos(la1 * Math.PI / 180) * Math.cos(la2 * Math.PI / 180) *
            Math.sin(dLo / 2) * Math.sin(dLo / 2);
    return EARTH_MI * 2 * Math.asin(Math.sqrt(a));
  }

  // Nearest grid point to a lat/lon. Linear scan over ~2082 points — runs once
  // per settled ZIP, sub-millisecond. Returns the point or null on empty grid.
  function nearestPoint(lat, lon, grid) {
    var best = null, bestD = Infinity;
    if (!grid) return null;
    for (var i = 0; i < grid.length; i++) {
      var d = haversine(lat, lon, grid[i].lat, grid[i].lon);
      if (d < bestD) { bestD = d; best = grid[i]; }
    }
    return best;
  }

  // Resolve a ZIP to a solar record {kwh_day_avg, kwh_day_summer, kwh_day_winter}.
  // override -> nearest grid -> null.
  function getSolar(zip, solarData, zipLatLon) {
    zip = String(zip == null ? '' : zip).trim();
    if (solarData && solarData.zip_overrides && solarData.zip_overrides[zip]) {
      return solarData.zip_overrides[zip];
    }
    var ll = zipLatLon && zipLatLon[zip];
    if (!ll || typeof ll.lat !== 'number' || typeof ll.lon !== 'number') return null;
    return nearestPoint(ll.lat, ll.lon, solarData && solarData.grid);
  }

  // The dollars-of-charging-skipped framing. annualKwh * $/kWh.
  function offsetDollars(annualKwh, ratePerKwh) {
    var rate = (typeof ratePerKwh === 'number' && ratePerKwh > 0) ? ratePerKwh : 0.15;
    return Math.round(annualKwh * rate);
  }

  // Full result for a ZIP + battery + electricity rate. Returns null if the ZIP
  // can't be resolved to any solar data at all.
  //   battery: 'lead' | 'lead-acid' | 'lithium'
  //   ratePerKwh: $/kWh for the savings line (default 0.15)
  function compute(zip, battery, ratePerKwh, data) {
    data = data || {};
    var solar = getSolar(zip, data.solarData, data.zipLatLon);
    if (!solar || typeof solar.kwh_day_avg !== 'number') return null;

    var mpk = milesPerKwh(battery);
    var ll = (data.zipLatLon && data.zipLatLon[String(zip).trim()]) || null;
    var city = (ll && ll.city) || null;
    var state = (ll && ll.state) || null;
    var exact = !!city;

    var perDay = round1(solar.kwh_day_avg * mpk);
    var summer = round1(solar.kwh_day_summer * mpk);
    var winter = round1(solar.kwh_day_winter * mpk);
    var annualKwh = Math.round(solar.kwh_day_avg * 365);

    return {
      ok: true,
      exact: exact,
      city: city,
      state: state,
      place: exact ? (city + ', ' + state) : null,
      battery: (battery === 'lithium') ? 'lithium' : 'lead-acid',
      milesPerKwh: mpk,
      dailyKwh: Math.round(solar.kwh_day_avg * 100) / 100,
      perDay: perDay,
      summer: summer,
      winter: winter,
      perYear: Math.round(perDay * 365),
      annualKwh: annualKwh,
      dollars: offsetDollars(annualKwh, ratePerKwh)
    };
  }

  var RRCalc = {
    MI_PER_KWH: MI_PER_KWH,
    validZip: validZip,
    round1: round1,
    milesPerKwh: milesPerKwh,
    haversine: haversine,
    nearestPoint: nearestPoint,
    getSolar: getSolar,
    offsetDollars: offsetDollars,
    compute: compute
  };

  // Browser: attach to global. Node/vitest: also export.
  if (typeof globalThis !== 'undefined') { globalThis.RRCalc = RRCalc; }
  else if (typeof window !== 'undefined') { window.RRCalc = RRCalc; }
  if (typeof module !== 'undefined' && module.exports) { module.exports = RRCalc; }
})();
